/**
 * CR229: Template Resolution & Fallback System
 * 
 * T0 + T5: Centralized template resolution with DEFAULT_GENERIC fallback.
 * This ensures no deal card ever breaks due to missing template config.
 */

import type { CardTemplateJson, CardTemplateSections, CardTemplateLabels } from "@/providers/DealCardConfigProvider";

// ================================================================
// DEFAULT TEMPLATE - THE ULTIMATE FALLBACK
// ================================================================

export const DEFAULT_GENERIC_SECTIONS: CardTemplateSections = {
  headline: true,
  promo_badges: true,
  details_pills: true,
  dates: true,
  effective_value_box: false,
  qualifying_spend_box: false,
  reward_box: false,
  eligibility_box: false,
  source_buttons: true,
  why_deal_accordion: true,
};

export const DEFAULT_GENERIC_LABELS: CardTemplateLabels = {
  effective_value_title: "Effective Value",
  reward_title: "Reward",
  qualifying_spend_title: "Qualifying Spend",
  eligibility_title: "Eligibility",
};

export const DEFAULT_GENERIC_TEMPLATE: CardTemplateJson = {
  sections: DEFAULT_GENERIC_SECTIONS,
  labels: DEFAULT_GENERIC_LABELS,
};

// ================================================================
// DEAL TYPE-AWARE DEFAULTS
// ================================================================

/**
 * Deal-type aware default templates.
 * When creating a new template for a deal type, seed these sensible defaults.
 */
export const DEAL_TYPE_DEFAULTS: Record<string, Partial<CardTemplateJson>> = {
  BONUS: {
    sections: {
      ...DEFAULT_GENERIC_SECTIONS,
      reward_box: true,
      qualifying_spend_box: true,
      effective_value_box: true,
    },
    labels: {
      ...DEFAULT_GENERIC_LABELS,
      reward_title: "Bonus",
      qualifying_spend_title: "Spend Required",
    },
  },
  POINTS: {
    sections: {
      ...DEFAULT_GENERIC_SECTIONS,
      reward_box: true,
      qualifying_spend_box: true,
    },
    labels: {
      ...DEFAULT_GENERIC_LABELS,
      reward_title: "Points",
      qualifying_spend_title: "Spend Required",
    },
  },
  DISCOUNT: {
    sections: {
      ...DEFAULT_GENERIC_SECTIONS,
      effective_value_box: true,
    },
    labels: {
      ...DEFAULT_GENERIC_LABELS,
      effective_value_title: "Savings",
    },
  },
  IN_KIND: {
    sections: {
      ...DEFAULT_GENERIC_SECTIONS,
      reward_box: true,
    },
    labels: {
      ...DEFAULT_GENERIC_LABELS,
      reward_title: "Free Gift",
    },
  },
  UNCLASSIFIED: {
    sections: DEFAULT_GENERIC_SECTIONS,
    labels: DEFAULT_GENERIC_LABELS,
  },
};

// ================================================================
// TEMPLATE RESOLUTION RESULT TYPE
// ================================================================

export type TemplateSource = "exact" | "fallback";

export interface TemplateResolutionResult {
  /** The resolved template */
  template: CardTemplateJson;
  /** Whether this is an exact match or fallback */
  source: TemplateSource;
  /** If fallback, what key was used */
  fallbackFrom?: string;
  /** The deal type key that was requested */
  requestedType: string;
}

// ================================================================
// TEMPLATE RESOLUTION FUNCTION
// ================================================================

/**
 * Resolve a template for a given deal type.
 * 
 * Priority:
 * 1. Exact match in templates map
 * 2. DEFAULT_GENERIC fallback
 * 
 * @param dealTypeKey - The deal type to get template for (e.g., "BONUS", "DISCOUNT")
 * @param templatesMap - Map of deal_type_key to template from DB
 * @returns Resolution result with template and source metadata
 */
export function getTemplateForDealType(
  dealTypeKey: string,
  templatesMap: Map<string, CardTemplateJson> | Record<string, CardTemplateJson>
): TemplateResolutionResult {
  const normalizedKey = dealTypeKey.toUpperCase();
  
  // Convert to Map if needed
  const templates = templatesMap instanceof Map 
    ? templatesMap 
    : new Map(Object.entries(templatesMap));
  
  // Try exact match
  const exactTemplate = templates.get(normalizedKey);
  if (exactTemplate) {
    return {
      template: mergeWithDefaults(exactTemplate),
      source: "exact",
      requestedType: normalizedKey,
    };
  }
  
  // Fallback to DEFAULT_GENERIC
  return {
    template: DEFAULT_GENERIC_TEMPLATE,
    source: "fallback",
    fallbackFrom: "DEFAULT_GENERIC",
    requestedType: normalizedKey,
  };
}

/**
 * Merge a partial template with defaults to ensure all fields exist.
 */
export function mergeWithDefaults(partial: Partial<CardTemplateJson>): CardTemplateJson {
  return {
    sections: {
      ...DEFAULT_GENERIC_SECTIONS,
      ...partial.sections,
    },
    labels: {
      ...DEFAULT_GENERIC_LABELS,
      ...partial.labels,
    },
  };
}

/**
 * Get the default template for a new deal type.
 * Uses deal-type-aware defaults when available.
 */
export function getDefaultTemplateForType(dealTypeKey: string): CardTemplateJson {
  const normalizedKey = dealTypeKey.toUpperCase();
  const typeDefaults = DEAL_TYPE_DEFAULTS[normalizedKey];
  
  if (typeDefaults) {
    return mergeWithDefaults(typeDefaults);
  }
  
  return DEFAULT_GENERIC_TEMPLATE;
}

/**
 * Check if a deal type has an exact template match.
 */
export function hasExactTemplate(
  dealTypeKey: string,
  templatesMap: Map<string, CardTemplateJson> | Record<string, CardTemplateJson>
): boolean {
  const normalizedKey = dealTypeKey.toUpperCase();
  const templates = templatesMap instanceof Map 
    ? templatesMap 
    : new Map(Object.entries(templatesMap));
  return templates.has(normalizedKey);
}
